/**
 * **The wall** (the ninth overnight; the Watchtower TV board): one screen, no mouse, for the TV in
 * the Watchtower room. It cycles the board, the gate, the weather and the open incidents, fifteen
 * seconds each, and reads the board again once a minute. Read-only — nothing on it can be
 * pressed by somebody walking past (the Dispatch Wall spec); Escape goes back to the desk.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { formatCountDay, formatTime } from '@cider/ui';
import { go } from '../../shell/router.js';
import { useLoad } from '../admin/shared.js';
import { useWtYear, weatherLine, type Board } from './Watchtower.js';
import './watchtower.css';

const PANELS = ['board', 'gate', 'weather', 'incidents'] as const;
const DWELL_MS = 15_000;

export function Wall(): ReactNode {
  const year = useWtYear();
  const b = useLoad<Board>(`/ops/board?year=${String(year)}`);
  const { reload } = b;
  const [panel, setPanel] = useState(0);
  const [clock, setClock] = useState(new Date());
  useEffect(() => {
    const cycle = setInterval(() => {
      setPanel((p) => (p + 1) % PANELS.length);
    }, DWELL_MS);
    const read = setInterval(reload, 60_000);
    const tick = setInterval(() => {
      setClock(new Date());
    }, 1_000);
    const back = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') go('/ops/watchtower/desk');
    };
    window.addEventListener('keydown', back);
    return () => {
      clearInterval(cycle);
      clearInterval(read);
      clearInterval(tick);
      window.removeEventListener('keydown', back);
    };
  }, [reload]);
  const d = b.data;
  const which = PANELS[panel] ?? 'board';
  return (
    <div className="wall" data-screen="wall" data-panel={which}>
      <div className="wall__bar">
        <span>APPLE FEST · WATCHTOWER{d?.day ? ` · ${formatCountDay(d.day)}` : ''}</span>
        <span>{formatTime(clock)}</span>
      </div>
      {!d ? (
        <div className="wall__panel">
          <div className="wall__big">{b.error ?? 'Reading the board…'}</div>
        </div>
      ) : which === 'board' ? (
        <div className="wall__panel">
          <div className="wall__title">Today</div>
          <div className="wall__row">
            <span>On site now</span>
            <b>{d.numbers.onSite === null ? 'NO DATA' : String(d.numbers.onSite)}</b>
          </div>
          <div className="wall__row">
            <span>Checked in today</span>
            <b>
              {d.numbers.checkedInToday === null ? 'NO DATA' : String(d.numbers.checkedInToday)}
            </b>
          </div>
          {d.tiles.slice(0, 8).map((t) => (
            <div className="wall__row" key={t.areaId}>
              <span>{t.area}</span>
              <span>
                {t.here === null ? 'NO DATA' : `${String(t.here)} here`} ·{' '}
                {t.readiness === 'walked'
                  ? 'walked'
                  : t.readiness === 'open items'
                    ? 'a problem'
                    : 'not yet walked'}
              </span>
            </div>
          ))}
        </div>
      ) : which === 'gate' ? (
        <div className="wall__panel">
          <div className="wall__title">The gates</div>
          <div className="wall__big">{d.gate.says}</div>
        </div>
      ) : which === 'weather' ? (
        <div className="wall__panel">
          <div className="wall__title">Weather</div>
          <div className="wall__big">{weatherLine(d.weather)}</div>
          {d.weather.forecast?.periods.map((p) => (
            <div className="wall__row" key={p.name}>
              <span>{p.name}</span>
              <span>{p.text}</span>
            </div>
          ))}
          {!d.weather.forecast ? <div>{d.weather.forecastSays}</div> : null}
        </div>
      ) : (
        <div className="wall__panel">
          <div className="wall__title">Open incidents</div>
          {d.incidents.rows.length === 0 ? <div className="wall__big">Nothing open.</div> : null}
          {d.incidents.rows.map((i) => (
            <div className="wall__row" key={i.id}>
              <span>
                {formatTime(i.openedAt)} · {i.kindWords}
                {i.place ? ` · ${i.place}` : ''}
              </span>
              <span>{i.going ? `${i.going} going` : 'nobody assigned'}</span>
            </div>
          ))}
        </div>
      )}
      <div className="wall__dots" aria-hidden="true">
        {PANELS.map((p, n) => (
          <span key={p} className={`wall__dot${n === panel ? ' wall__dot--on' : ''}`} />
        ))}
      </div>
    </div>
  );
}
